import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
    ViewChild, AfterContentInit,
    ViewEncapsulation,
    OnInit,
    OnChanges,
    SimpleChanges,
    inject,
    signal,
    effect,
    input
} from '@angular/core';
import { BaseListView } from '../../../view/base-listview';
import { RequestHandler } from '../../../service/OffService/request-handler';
import { Filtering } from '../../../service/filtering/filtering';
import { Utils } from '../../utils';
import { Table, TablePageEvent } from 'primeng/table';
import { DashboardService } from '../../../../app/service/events/dashboard.service';
import { environment } from '../../../../environments/environment';
import { StorageManager } from '../../../common/storage-manager.class';

import { NgForOfContext } from '@angular/common';
import { ColInterface } from './col.interface';
import { ColStylesInterface } from './col-styles.interface';
import { TreeTableSortEvent } from 'primeng/treetable';
import { MenuItem, TreeNode, TreeTableNode } from 'primeng/api';
import { NavigationService } from 'src/app/service/navigation.service';


@Component({
    selector: 'app-common-list-table',
    standalone: false,
    encapsulation: ViewEncapsulation.None,
    templateUrl: './common-list-table.component.html',
    styleUrls: ['./common-list-table.component.scss']
})
export class CommonListTableComponent<T> extends BaseListView<T> implements OnInit, OnChanges, AfterContentInit {

    private static oldCurrentTitle = '';

    @Input()
    override request = new RequestHandler<any>();

    @Input()
    override searchOn: string[] = [];

    @Input()
    override searchPriority: string[] = [];

    @Input() enableFilter: (() => undefined) | undefined = undefined;

    @Input() cols: ColInterface[] = [];

    @Input() customData: any[] = [];

    @Input() actionButtonText = '';

    @Input() searchValue: { term: string } = { term: '' };

    @Input() override filtering = new Filtering<T>();

    @Input() selection: string | never[] = '';

    selectionMode = input<'single' | 'multiple' | 'checkbox' | null>('single');

    @Input()
    hideSearch = false;

    @Input()
    hidePaginator = false;

    @Input()
    showExportCsv = true;

    @Input()
    customExportCsv = false;

    @Input() first = 0;

    @Input() visible = true;

    @Input() small = false;

    @Input() showStockReto = false;

    @Input() editButtonsPosition = 'top';

    @Output() actionButtonClick: EventEmitter<object> = new EventEmitter<object>();

    @Output() saveButtonClick: EventEmitter<object> = new EventEmitter<object>();

    @Output() undoButtonClick: EventEmitter<object> = new EventEmitter<object>();

    @Output() emitSearchText: EventEmitter<string> = new EventEmitter<string>();

    @Output() filterPressed: EventEmitter<object> = new EventEmitter<object>();

    @Output() rowClick: EventEmitter<TreeNode<T> | TreeNode<T>[]> = new EventEmitter<TreeNode<T> | TreeNode<T>[]>();

    @Output() fieldEdit: EventEmitter<{ value: string | Date; data: TreeNode<T>; from: string; }> =
        new EventEmitter<{ value: string | Date; data: TreeNode<T>; from: string; }>();

    @Output() exportCsvClick: EventEmitter<object> = new EventEmitter<object>();

    @Output() exportRetoClick: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output() visualize: EventEmitter<T> = new EventEmitter<T>();

    @Output() edit: EventEmitter<T> = new EventEmitter<T>();

    @Output() duplicate: EventEmitter<T> = new EventEmitter<T>();

    @Output() remove: EventEmitter<T> = new EventEmitter<T>();

    @ContentChild('thEnd', { read: TemplateRef, static: false }) thEnd: TemplateRef<NgForOfContext<object, object[]>> | undefined;

    @ContentChild('thStart', { read: TemplateRef, static: false }) thStart: TemplateRef<NgForOfContext<object, object[]>> | undefined;

    @ContentChild('tdEnd', { read: TemplateRef, static: false }) tdEnd: TemplateRef<NgForOfContext<object, object[]>> | undefined;

    @ContentChild('tdStart', { read: TemplateRef, static: false }) tdStart: TemplateRef<NgForOfContext<object, object[]>> | undefined;

    @ContentChild('header', { read: TemplateRef, static: false }) header: TemplateRef<NgForOfContext<any, any>> | undefined;

    @ContentChild('headerActions', { read: TemplateRef, static: false }) 
    headerActions: TemplateRef<NgForOfContext<object, object[]>> | undefined;

    @ContentChild('center', { read: TemplateRef, static: false }) center: TemplateRef<NgForOfContext<any, any>> | undefined;

    @ContentChild('caption', { read: TemplateRef, static: false }) caption: TemplateRef<NgForOfContext<object, object[]>> | undefined;

    @ContentChild('footer', { read: TemplateRef, static: false }) footer: TemplateRef<NgForOfContext<object, object[]>> | undefined;

    @ViewChild('pTable', { read: Table, static: true })
    pTable: Table | undefined;

    public appName = environment.appName;

    public rol = StorageManager.getUser().rol;

    public selected = {} as T;

    public hasEditableFields = false;

    public currentTitle = '';

    public currentPage = {} as TablePageEvent;

    public selectedNode = signal<TreeNode[] | TreeNode | null>(null);

    public menuItems: MenuItem[] = [];

    private cachedNodes: TreeNode<T>[] = [];

    private lastFilteringValue = '';

    tableLarge = signal<boolean>(false);

    navigationService = inject(NavigationService);

    selectColumns = signal<ColInterface[]>([]);

    constructor(private readonly dashboard: DashboardService) {
        super();

        effect(() => {
            const currentMode = this.selectionMode();
            const nodos = this.selectedNode();

            if (currentMode === 'checkbox' && Array.isArray(nodos)) {
                this.rowClick.emit(nodos ?? []);
            }
            this.selectedNode.set(null);
        });
    }


    get treeNodes(): TreeNode<T>[] {
        if (this.customData.length > 0) {
            return this.transformToTreeNodes(this.customData);
        }

        const currentFilteringValue = JSON.stringify(this.filtering.value);
        
        if (!this.cachedNodes.length || this.lastFilteringValue !== currentFilteringValue) {
            this.cachedNodes = this.transformToTreeNodes(this.filtering.value);
            this.lastFilteringValue = currentFilteringValue;
        }

        return this.cachedNodes;
    }


    @Input() customSort: ((event: TreeTableSortEvent) => void) = () => { };

    @Input() globalVisualTransform: (value: string, col: ColInterface, row: TreeNode<T>) => string = () => '';

    @Input() globalConditionalStyle: (
        value: string,
        col: ColInterface,
        colGroup: ColInterface[],
        row: TreeNode<T>
    ) => ColStylesInterface = () => ({});


    ngOnChanges(changes: SimpleChanges): void {

        if (changes['searchValue']) {
            this.setGlobalContentFilter(changes['searchValue']?.currentValue.term);
            this.searchValue = (this.searchValue || { term: '' });
        }

        if (changes['cols']) {
            const visibleCols = this.cols.filter((it: ColInterface) => it && it.visible !== false);

            if (visibleCols.length > 12) {
                this.tableLarge.set(true);
            }

            this.cols = this.cols.filter((it: ColInterface) => it && it.visible !== false);
        }
    }


    ngOnInit(): void {
        this.currentTitle = this.appName + '_' + this.dashboard.headerTitle;
        this.initRequest(this.request);
        this.selection = (this.selection || []);
        this.searchValue = (this.searchValue || { term: '' });
        this.customSort = this.customSort || this.defaultSort;
        this.configureOnPageChange();

        if (this.checkOldTitle(this.currentTitle)) {
            this.removeTempData();
        } else {
            this.getTableState(this.request);
        }

        // this.dashboard.menuItemChanges$.subscribe(item => {
        //     if (item !== title) {
        //         this.removeTempData();
        //     }
        // });

        if (this.cols) {
            this.cols.forEach((col: ColInterface) => {
                switch (col.filter) {
                    case 'dropdown':
                        this.setDropdownSelectOn('', col.field);
                        break;
                    case 'multiselect':
                        this.setMultiSelectOn('', col.field);
                        break;
                    case 'calendar':
                        this.setDateRangeOn();
                        break;
                }
                if (col.editable) {
                    this.hasEditableFields = true;
                }
            });
        }
    }

    ngAfterContentInit(): void {

        if (this.selectionMode() !== 'checkbox') {
            const add = (
                emitter: EventEmitter<any>,
                item: MenuItem,
            ) => {
                if ((emitter as any).observed ?? emitter.observers.length) {
                    this.menuItems.push(item);
                }
            };

            const getSelectedNode = (): TreeNode<T> | null | undefined => {
                const currentSelection = this.selectedNode();

                if (Array.isArray(currentSelection)) {
                    return currentSelection.length > 0 ? (currentSelection[0] as TreeNode<T>) : null;
                }
                return currentSelection as TreeNode<T> | null | undefined;
            };

            add(this.visualize, {
                label: 'Visualizar',
                icon: 'pi pi-info',
                command: () => {
                    const node = getSelectedNode();

                    if (node && node.data !== null ) { this.visualize.emit(node.data); }
                }
            });

            add(this.edit, {
                label: 'Editar',
                icon: 'pi pi-pencil',
                command: () => {
                    const node = getSelectedNode();

                    if (node && node.data !== null) { this.edit.emit(node.data); }
                }
            });

            add(this.duplicate, {
                label: 'Duplicar',
                icon: 'pi pi-copy',
                command: () => {
                    const node = getSelectedNode();

                    if (node && node.data !== null) { this.duplicate.emit(node.data); }
                }
            });

            add(this.remove, {
                label: 'Eliminar',
                icon: 'pi pi-trash',
                command: () => {
                    const node = getSelectedNode();

                    if (node && node.data !== null) { this.remove.emit(node.data); }
                }
            });
        }

    }


    transformToTreeNodes(data: TreeNode<T>[] | void): TreeNode<T>[] {
        if (data && Array.isArray(data)) {
            return data.map((item) => ({
                data: item as T, children: [], leaf: true
            }));
        } else {
            return [];
        }
    }

    checkOldTitle(new_title: string): boolean {
        if (CommonListTableComponent.oldCurrentTitle == null || CommonListTableComponent.oldCurrentTitle.length === 0) {
            CommonListTableComponent.oldCurrentTitle = new_title;
            return false;
        }
        if (CommonListTableComponent.oldCurrentTitle.localeCompare(new_title) === 0) {
            return false;
        }
        if (CommonListTableComponent.oldCurrentTitle.localeCompare(new_title) !== 0) {
            if (new_title.toLowerCase().includes('edit')) {
                return false;
            }
            CommonListTableComponent.oldCurrentTitle = new_title;
            return true;
        }
        return false;

    }

    exportReto(): void {
        this.exportRetoClick.emit(true);
    }

    showAddButton(): boolean {
        if (StorageManager.getUser().rol === 'demo') {
            return false;
        }
        return true;
    }

    onRightClick(event: any) {
        if (event.data) {
            this.selectedNode.set(event);
        }
    }

    nodeSelect(event: TreeTableNode): void {

        console.log(event);
        
        
        if (this.selectionMode() !== 'checkbox') {
            if (event.type === 'row') {
                const target = event.originalEvent?.target as HTMLElement;
                const selectedText = window.getSelection()?.toString();

                if (selectedText) {
                    this.selectedNode.set(null);
                    return;
                }

                if (target.closest('button')) {
                    this.selectedNode.set(null);
                    return;
                }

                this.rowClick.emit(event.node);
                this.edit.emit((this.selectedNode() as any)?.data);
            }
        }
    }

    public getTableState(request: RequestHandler<T>) {

        if (this.currentTitle.includes('Editar Cliente')) {
            this.currentTitle = this.currentTitle.replace('Editar Cliente', 'Clientes');
            //CommonListTableComponent.oldCurrentTitle = this.currentTitle;
        }
        const localSearchValue = localStorage.getItem(this.currentTitle + '_TEMP_SEARCH_TEXT') || '';
        const localCurrentPage = JSON.parse(localStorage.getItem(this.currentTitle + '_TEMP_PAGE_NUMBER') ?? '{}') || {};

        if (request.value) {
            if (localCurrentPage?.first && this.pTable) {
                this.pTable.first = localCurrentPage.first;
            }
            if (localSearchValue) {
                this.searchValue = { 'term': localSearchValue };
                this.filters.global = localSearchValue;
            }

        } else {
            request.response(() => {
                if (localCurrentPage?.first && this.pTable) {
                    this.pTable.first = localCurrentPage.first;
                }
                if (localSearchValue) {
                    this.searchValue = { 'term': localSearchValue };
                    this.filters.global = localSearchValue;
                }

            });
        }

        this.setGlobalContentFilter(localSearchValue);
    }

    public setTableState(): void {

        if (this.searchValue.term) {
            localStorage.setItem(this.currentTitle + '_TEMP_SEARCH_TEXT', this.searchValue.term);
        }
        if (this.currentPage) {
            localStorage.setItem(this.currentTitle + '_TEMP_PAGE_NUMBER', JSON.stringify(this.currentPage));
        } else if (this.pTable && this.pTable.first !== 0) {
            localStorage.setItem(this.currentTitle + '_TEMP_PAGE_NUMBER', JSON.stringify({
                first: this.pTable.first,
                rows: this.pTable.rows
            }));
        }
    }

    public removeTempData(): void {
        localStorage.removeItem(this.currentTitle + '_TEMP_SEARCH_TEXT');
        localStorage.removeItem(this.currentTitle + '_TEMP_PAGE_NUMBER');
    }

    public setSearchText(value: string | null): void {
        if (value == null || (value as string).length === 0) {
            this.removeTempData();
        } else {
            this.emitSearchText.emit(value);

            if (this.pTable) {
                this.pTable.first = 0;
            }

            localStorage.removeItem(this.currentTitle + '_TEMP_PAGE_NUMBER');
            this.setTableState();
        }

    }

    public editField(event: any, data: TreeNode<T>, from: string): void {
        // PrimeNG: onInput gives DOM event; ngModelChange gives value directly
        const value = (event?.value ?? event?.target?.value ?? event) as string | Date;

        this.fieldEdit.emit({ value, data, from });
    }


    public exportCsv(): void {
        // Si el usuario ha definido una función personalizada para procesar los datos, simplemente los devuelve.
        // Si no, les aplica el proceso genérico para convertir a CSV: descargar la tabla tal cual está.
        if (this.customExportCsv) {

            this.exportCsvClick.emit({
                cols: this.cols,
                value: this.filtering.value
            });
            // return;
        }

        const charset = 'windows-1252';
        let csv =
            'data:text/csv;charset=' +
            charset +
            ',\ufeff' +
            (this.cols || []).map((it: { header: string; }) => it.header).join(';') +
            '\n';

        (this.filtering.value || []).forEach(row => {
            this.cols.forEach((col: ColInterface) => {
                let str: string = this.processCellVisualTransform(row, col) ?? '';

                if ([undefined, 'undefined', null, 'null', NaN, 'NaN'].includes(str)) {
                    str = '';
                } else {
                    str = str.toString().replace('#', '');
                }
                csv += '"' + (str || '').replace(/"/g, '\\"') + '";';
                //csv += str + ';';
            });
            csv = csv.slice(0, -1) + '\n';
        });

        const csvLink = document.getElementById('csvLink') as HTMLAnchorElement;

        csvLink.download = this.currentTitle + '_exportado.csv';
        csvLink.href = encodeURI(csv);
        csvLink.click();
    }

    public processCellVisualTransform(data: TreeNode<T>, col: ColInterface) {
        let finalTransform;
        //AQUI RECOGIDA DE FIELDS PARA COLUMNA p-table

        if (col.visualTransform) {
            finalTransform = col.visualTransform;
        } else if (this.globalVisualTransform) {
            if (data.data) {
                finalTransform = this.globalVisualTransform((data.data as Record<string, string>)[col.field] ?? '', col, data);
            } else {
                finalTransform = this.globalVisualTransform((data as Record<string, string>)[col.field] ?? '', col, data);
            }
        } else {
            finalTransform = (data.data as Record<string, string>)[col.field];
        }

        return finalTransform;
    }

    public processCellConditionalStyle(data: TreeNode<T>, col: ColInterface): ColStylesInterface {
        let finalStyle: ColStylesInterface = {};

        if (col.style) {
            finalStyle = col.style;
        } else if (this.globalConditionalStyle) {
            finalStyle = this.globalConditionalStyle((data.data as Record<string, string>)[col.field ?? ''] ?? '', col, this.cols, data);

            if (finalStyle?.style) {
                finalStyle = finalStyle.style;
            }
        }

        return finalStyle ? finalStyle : {};
    }

    public processRowConditionalStyle(data: TreeNode<T>) {
        let finalStyle;

        for (const columna of this.cols) {
            const col = columna;

            if (col.rowStyle) {
                finalStyle = col.rowStyle;
                break;
            } else if (this.globalConditionalStyle) {
                finalStyle = this.globalConditionalStyle((data.data as Record<string, string>)[col.field] ?? '', col, this.cols, data);

                if (finalStyle?.rowStyle) {
                    finalStyle = finalStyle.rowStyle;
                    break;
                } else {
                    finalStyle = null;
                }
            }
        }

        return finalStyle ? finalStyle : {};
    }

    public defaultSort(event: TreeTableSortEvent) {
        const { data = [], field = '', order = 1 } = event;

        const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;

        data.sort((a, b) => {

            let v1 = a.data?.[field];
            let v2 = b.data?.[field];

            const m1 = typeof v1 === 'string' && ddmmyyyy.exec(v1);
            const m2 = typeof v2 === 'string' && ddmmyyyy.exec(v2);

            if (m1 && m2) {
                v1 = new Date(+(m1[3] ?? 0), +(m1[2] ?? 0) - 1, +(m1[1] ?? 0));
                v2 = new Date(+(m2[3] ?? 0), +(m2[2] ?? 0) - 1, +(m2[1] ?? 0));
            } else {
                const d1 = this.toDate(v1);
                const d2 = this.toDate(v2 as string);

                if (d1 && d2) { v1 = d1; v2 = d2; }
                else if (Utils.isNumber(v1 as string) && Utils.isNumber(v2 as string)) {
                    v1 = parseFloat(v1 as string);
                    v2 = parseFloat(v2 as string);
                }
            }

            let result = 0;

            if (v1 == null && v2 != null) {
                result = -1;
            } else if (v1 != null && v2 == null) {
                result = 1;
            } else if (v1 == null && v2 == null) {
                result = 0;
            } else if (typeof v1 === 'string' && typeof v2 === 'string') {
                result = v1.localeCompare(v2);
            } else {
                result = (v1 < v2 ? -1 : 1 > 0) as number;
            }

            return order * result;
        });
    }


    public getFullCellValue(data: TreeNode<T>, col: ColInterface): string {
        if (!data || !data.data) {
            return '';
        };

        if (this.globalVisualTransform) {
            const rawValue = (data.data as Record<string, string>)[col.field] ?? '';
            const transformed = this.globalVisualTransform(rawValue, col, data);
            if (transformed && transformed !== rawValue) {
                return transformed;
            }
        }

        const value = (data.data as any)[col.field];

        if (value == null) {
            return '';
        };

        if (Array.isArray(value)) {
            const max = 3;
            const visibles = value.slice(0, max).map(v => this.formatNumber(v)).join(', ');
            const more = value.length > max ? '...' : '';
            return visibles + more;
        }

        if (typeof value === 'string') {

            const values = value
                .split(';')
                .map(v => v.trim())
                .filter(v => v.length > 0);

            if (values.length > 1) {
                const max = 3;
                const visibles = values.slice(0, max).map(v => this.formatNumber(v)).join(', ');
                const more = values.length > max ? '...' : '';
                return visibles + more;
            }

            return this.formatNumber(value);
        }

        return this.formatNumber(value?.toString() ?? '');
    }

    private formatNumber(value: any): string {
        const str = value?.toString() ?? '';
        
        // Detectar si es un número
        const num = parseFloat(str.replace(',', '.'));
        
        if (!isNaN(num) && /^-?\d+([.,]\d+)?$/.test(str.trim())) {
            // Redondear a máximo 3 decimales
            const rounded = Math.round(num * 1000) / 1000;
            
            // Formatear con punto para miles y coma para decimales
            const parts = rounded.toString().split('.');
            const integerPart = parts[0]?.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            const decimalPart = parts[1] ? ',' + parts[1] : '';
            
            return integerPart + decimalPart;
        }
        
        return str;
    }


    private toDate(value: unknown): Date | null {
        if (value instanceof Date) { return value; }

        if (typeof value === 'string') {
            const iso = new Date(value);

            if (!isNaN(iso.getTime())) { return iso; }

            const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;
            const m = ddmmyyyy.exec(value);

            if (m) {
                const [, d, mth, y] = m;

                return new Date(+(y ?? 0), +(mth ?? 0) - 1, +(d ?? 0));
            }
        }
        return null;
    }



    private configureOnPageChange() {
        if (this.pTable) {
            this.pTable.onPageChange = (event => {
                this.currentPage = event;

                if (this.pTable) {
                    this.pTable.first = event.first;
                    this.pTable.rows = event.rows;
                    if (this.pTable.lazy) {
                        this.pTable.onLazyLoad.emit(this.pTable.createLazyLoadMetadata());
                    }
                    this.pTable.onPage.emit({
                        first: this.pTable.first,
                        rows: this.pTable.rows
                    });
                    this.pTable.tableService.onValueChange(this.pTable.value);
                }
            });
        }
    }
}
